import { useHistory, useLocation, Link } from 'react-router-dom'
import { Button, PageHeader, Select, Tag, type TagProps } from 'antd'

import {
  AddonType,
  useAddonSummaryFragment,
  useDeleteSchemeAddonMutation,
} from '~/gql/generated/graphql'

import * as routes from '~/constants/routes'
import { PermissionOnly } from '~/components/Session'

const tagColours: Record<AddonType, TagProps['color']> = {
  [AddonType.free_unlocks]: 'green',
}

export const addonTypes: Record<AddonType, string> = {
  [AddonType.free_unlocks]: 'Free unlocks',
}

export const AddonTypeTag = ({ addonType }: { addonType: AddonType }) => (
  <Tag color={tagColours[addonType]}>{addonTypes[addonType]}</Tag>
)

export const AddonTypeSelect = (props: React.ComponentProps<typeof Select>) => {
  const options = Object.keys(AddonType).map(item => ({
    value: item,
    label: addonTypes[item],
  }))
  return <Select options={options} {...props} />
}

type AddonPageHeaderProps = {
  id: string
  schemeId: string
  goBack?: boolean
  isEditing?: boolean
}
export const AddonPageHeader = ({
  id,
  schemeId,
  goBack,
  isEditing,
}: AddonPageHeaderProps) => {
  // Use data from cache for headline fields to show them while the detail query is loading
  // This fragment is fetched by the list view
  const { data: fragmentData } = useAddonSummaryFragment({ id })
  const [removeSchemeAddon, { loading: deleting }] =
    useDeleteSchemeAddonMutation({ variables: { schemeAddonId: id } })
  const history = useHistory()
  const location = useLocation()

  const edit = () =>
    history.push(routes.prependScheme(`/addons/${id}/edit`, schemeId))

  const deleteAddon = async () => {
    await removeSchemeAddon()
    history.push(routes.prependScheme('/addons', schemeId))
  }

  return (
    <PageHeader
      title={fragmentData.name}
      subTitle={isEditing ? 'Editing' : fragmentData.id}
      tags={
        fragmentData.addon_type
          ? [
              <AddonTypeTag
                addonType={fragmentData.addon_type}
                key={fragmentData.addon_type}
              />,
            ]
          : []
      }
      extra={
        isEditing
          ? []
          : [
              <PermissionOnly permission="product.edit">
                <Button type="primary" onClick={edit} key="edit">
                  Edit
                </Button>
              </PermissionOnly>,
              <PermissionOnly permission="product.delete">
                <Button
                  type="primary"
                  onClick={deleteAddon}
                  loading={deleting}
                  key="delete"
                  danger
                >
                  Delete
                </Button>
              </PermissionOnly>,
            ]
      }
      onBack={goBack ? history.goBack : undefined}
      breadcrumb={{
        routes: [
          { path: '/addons', breadcrumbName: 'Addons' },
          {
            path: `/addons/${id}`,
            breadcrumbName: fragmentData.name ?? '...',
          },
          { path: `/addons/${id}/edit`, breadcrumbName: 'Edit' },
        ],
        itemRender(route) {
          const thisRouteWithScheme = routes.prependScheme(route.path, schemeId)
          const thisDepth = thisRouteWithScheme.split('/').length
          const locationDepth = location.pathname.split('/').length

          if (thisDepth > locationDepth) {
            return null
          }

          if (location.pathname === thisRouteWithScheme) {
            return <span>{route.breadcrumbName}</span>
          } else {
            return (
              <Link key={route.path} to={{ pathname: thisRouteWithScheme }}>
                {route.breadcrumbName}
              </Link>
            )
          }
        },
      }}
    />
  )
}
